<script>
  import { Route, router } from "tinro";
  import { onMount } from "svelte";
  import { getProfile } from "./utils/ApiCalls.svelte";
  import { loginToken } from "./components/DataStore";
  import LazyPage, { prefetchPage } from "./components/LazyPage.svelte";
  import { prefetchAllowed, runWhenIdle, whenLoaded } from "./utils/prefetch.js";
  import { loadOpusRecorder, loadSpeechSdk, loadWaveSurfer } from "./utils/libraries.js";

  import Login from "./pages/Login.svelte";

  /** A page's code together with the libraries it reads as globals when it starts, so it never starts without them. */
  const withLibraries = (page, ...libraries) => () =>
    Promise.all([page(), ...libraries.map((load) => load())]).then(([module]) => module);

  // Every page except the login page has its own code, loaded through LazyPage. The order is the order in which they
  // are fetched ahead (below): the pages a student goes to after signing in first, the pages for signing up last.
  const pages = {
    Dashboard: () => import("./pages/Dashboard.svelte"),
    CourseList: () => import("./pages/CourseList.svelte"),
    CourseDetail: () => import("./pages/CourseDetail.svelte"),
    LessonDetail: () => import("./pages/LessonDetail.svelte"),
    ExerciseOpen: withLibraries(
      () => import("./pages/ExerciseOpen.svelte"),
      loadSpeechSdk,
      loadWaveSurfer,
      loadOpusRecorder,
    ),
    RecordingList: () => import("./pages/RecordingList.svelte"),
    RecordingDetail: withLibraries(
      () => import("./pages/RecordingDetail.svelte"),
      loadSpeechSdk,
      loadWaveSurfer,
    ),
    PronunciationAnalyticsPage: withLibraries(
      () => import("./pages/PronunciationAnalyticsPage.svelte"),
      loadSpeechSdk,
    ),
    ReportPage: () => import("./pages/ReportPage.svelte"),
    TeachingComments: () => import("./pages/TeachingComments.svelte"),
    SpecialEventPage: () => import("./pages/SpecialEventPage.svelte"),
    CourseEnrollment: () => import("./pages/CourseEnrollment.svelte"),
    Settings: () => import("./pages/Settings.svelte"),
    AnalyticsPage: () => import("./pages/AnalyticsPage.svelte"),
    PremiumPage: () => import("./pages/PremiumPage.svelte"),
    GameLevels: () => import("./pages/GameLevels.svelte"),
    GameDetail: () => import("./pages/GameDetail.svelte"),
    ExamPage: () => import("./pages/ExamPage.svelte"),
    LessonTodo: () => import("./pages/LessonTodo.svelte"),
    Help: () => import("./pages/Help.svelte"),
    LoginQrCode: () => import("./pages/LoginQrCode.svelte"),
    Registration: () => import("./pages/Registration.svelte"),
    ResetPassword: () => import("./pages/ResetPassword.svelte"),
    ResetPasswordVerifyPage: () => import("./pages/ResetPasswordVerifyPage.svelte"),
    OneCampusPage: () => import("./pages/OneCampusPage.svelte"),
    SchoolPlusPage: () => import("./pages/SchoolPlusPage.svelte"),
  };

  // Everything is fetched ahead from the first page shown, usually the login page: once it has loaded and the
  // browser is idle, the code of every other page and the libraries they use, three at a time. Signing in and
  // opening any page afterwards then never waits for a download. Not with Save-Data or on a 2G connection: there a
  // page loads when it is opened.
  if (prefetchAllowed()) {
    whenLoaded().then(() => {
      performance.mark?.("prefetch-start"); // where scripts/measure-browser.mjs counts background downloads from
      return runWhenIdle(Object.values(pages).map((load) => () => prefetchPage(load)));
    });
  }

  router.mode.hash();

  // translations are set up before the app is mounted (src/i18n/index.js)

  onMount(() => {
    const url_string = window.location.href;
    if (url_string.match("one-campus")) {
      let urlParams = new URLSearchParams(document.location.search);

      //document.location.search = ''
      if (urlParams.get("dsns")) {
        router.goto(
          `/one-campus?dsns=${urlParams.get("dsns")}&code=${urlParams.get(
            "code",
          )}&role=${urlParams.get("role")}`,
        );
        return;
      }
    }

    if (url_string.match("schoolplus")) {
      let urlParams = new URLSearchParams(document.location.search);
      router.goto(
        `/schoolplus?code=${urlParams.get("code")}&role=${urlParams.get(
          "role",
        )}`,
      );
      return;
    }

    if (url_string.match("exkey")) {
      let urlParams = new URLSearchParams(document.location.search);
      router.goto(`exercise-open/${urlParams.get("exkey")}`);
    }

    getProfile();
  });
</script>

<Route>
  <Route path="/login" fallback>
    <Login />
  </Route>

  <Route path="/qr/:qrcode" let:meta>
    <LazyPage load={pages.LoginQrCode} props={{ qrcode: meta.params.qrcode, loginParam: meta.query.login }} />
  </Route>

  <Route path="/registration" let:meta>
    <LazyPage load={pages.Registration} props={{ emailParam: meta.query.email }} />
  </Route>

  <Route path="/one-campus" let:meta>
    <LazyPage
      load={pages.OneCampusPage}
      props={{ dsns: meta.query.dsns, code: meta.query.code, role: meta.query.role }}
    />
  </Route>

  <Route path="/schoolplus" let:meta>
    <LazyPage load={pages.SchoolPlusPage} props={{ code: meta.query.code, role: meta.query.role }} />
  </Route>

  <Route path="/reset-password">
    <LazyPage load={pages.ResetPassword} />
  </Route>

  <Route path="/reset-password-verify/:jwtToken" let:meta>
    <LazyPage load={pages.ResetPasswordVerifyPage} props={{ jwtToken: meta.params.jwtToken }} />
  </Route>

  {#if $loginToken}
    <Route path="/settings">
      <LazyPage load={pages.Settings} />
    </Route>

    <Route path="/help">
      <LazyPage load={pages.Help} />
    </Route>

    <Route path="/lesson-todo">
      <LazyPage load={pages.LessonTodo} />
    </Route>

    <Route path="/course-list">
      <LazyPage load={pages.CourseList} />
    </Route>

    <Route path="/course-enrollment/:coursePin" let:meta>
      <LazyPage load={pages.CourseEnrollment} props={{ coursePin: meta.params.coursePin }} />
    </Route>

    <Route path="/recording-list" let:meta>
      <LazyPage load={pages.RecordingList} props={{ courseId: meta.query.courseId }} />
    </Route>

    <Route path="/recording/:recordingId" let:meta>
      <LazyPage load={pages.RecordingDetail} props={{ recordingId: meta.params.recordingId }} />
    </Route>

    <Route path="/course/:courseId" let:meta>
      <LazyPage load={pages.CourseDetail} props={{ courseId: meta.params.courseId }} />
    </Route>

    <Route path="/lesson/:lessonId" let:meta>
      <LazyPage load={pages.LessonDetail} props={{ lessonId: meta.params.lessonId }} />
    </Route>

    <Route path="/exercise-open/:inputPin" let:meta>
      <LazyPage load={pages.ExerciseOpen} props={{ inputPin: meta.params.inputPin }} />
    </Route>

    <Route path="/premium">
      <LazyPage load={pages.PremiumPage} />
    </Route>

    <Route path="/exam">
      <LazyPage load={pages.ExamPage} />
    </Route>

    <Route path="/analytics">
      <LazyPage load={pages.AnalyticsPage} />
    </Route>

    <Route path="/pronunciation">
      <LazyPage load={pages.PronunciationAnalyticsPage} />
    </Route>

    <Route path="/pronunciation/:level/:item" let:meta>
      <LazyPage
        load={pages.PronunciationAnalyticsPage}
        props={{ level: meta.params.level, item: decodeURIComponent(meta.params.item) }}
      />
    </Route>

    <Route path="/report">
      <LazyPage load={pages.ReportPage} />
    </Route>

    <Route path="/teaching-comments">
      <LazyPage load={pages.TeachingComments} />
    </Route>

    <Route path="/events">
      <LazyPage load={pages.SpecialEventPage} />
    </Route>

    <Route path="/events/:eventId" let:meta>
      <LazyPage load={pages.SpecialEventPage} props={{ eventId: meta.params.eventId }} />
    </Route>

    <Route path="/games/:gameId/levels" let:meta>
      <LazyPage load={pages.GameLevels} props={{ gameId: meta.params.gameId }} />
    </Route>

    <Route path="/games/:gameId/levels/:levelId" let:meta>
      <LazyPage load={pages.GameDetail} props={{ gameId: meta.params.gameId, levelId: meta.params.levelId }} />
    </Route>

    <Route path="/">
      <LazyPage load={pages.Dashboard} />
    </Route>
    <Route path="/index">
      <LazyPage load={pages.Dashboard} />
    </Route>
  {/if}
</Route>
